import React, { useContext, useState, useEffect } from "react";
import "./Dashboard.css";
import { Context } from "../../utils/context";
import { findAll, findGames } from "../../utils/apis/dashboard/dashboard";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { formatAmount } from "../../utils/common";
import {
  FaUsers,
  FaUserSlash,
  FaSitemap,
  FaUserTie,
  FaUserShield,
  FaWallet,
  FaCoins,
  FaArrowDown,
  FaArrowUp,
  FaMoneyBillWave,
  FaUniversity,
  FaDice,
  FaClock,
  FaHandHoldingUsd,
  FaChevronRight,
  FaBuilding,
} from "react-icons/fa";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

// Per-game look (ids from backend IDS.Games); unknown games get a fallback.
const GAME_STYLE = {
  1: { icon: "🦋", color: "#a56bff" }, // Titli Soorat
  2: { icon: "🎰", color: "#ff5d7a" }, // Roulette
  3: { icon: "🃏", color: "#2ee6a6" }, // Teen Patti
  4: { icon: "♠️", color: "#4cc9ff" }, // Rummy
};
const GAME_FALLBACK = [
  { icon: "🎲", color: "#ff9f45" },
  { icon: "🎯", color: "#d4a73c" },
];

const ROLE_NAMES = {
  1: "Admin",
  2: "Super Super Master",
  3: "Super Master",
  4: "Master",
};

const formatCount = (n) =>
  new Intl.NumberFormat("en-IN").format(Number(n) || 0);

function StatCard({ label, value, icon: Icon, to, state, accent, loading }) {
  return (
    <Link
      to={to}
      state={state}
      className={`db-card ${accent ? `db-card-${accent}` : ""}`}>
      <div className="db-card-icon">
        <Icon />
      </div>
      <div className="db-card-body">
        <span className="db-card-label" title={label}>
          {label}
        </span>
        {loading ? (
          <span className="db-skeleton" />
        ) : (
          <span className="db-card-value">{value}</span>
        )}
      </div>
      <FaChevronRight className="db-card-arrow" />
    </Link>
  );
}

function Section({ title, subtitle, items, loading }) {
  if (!items.length) return null;
  return (
    <section className="db-section">
      <div className="db-section-head">
        <h4>{title}</h4>
        {subtitle && <span>{subtitle}</span>}
      </div>
      <div className="db-grid">
        {items.map((item) => (
          <StatCard key={item.label} {...item} loading={loading} />
        ))}
      </div>
    </section>
  );
}

// Part-to-whole split of a total into cash vs online, as one stacked bar.
function SplitBar({ title, cash, online, total, loading }) {
  const sum = cash + online;
  const cashPct = sum ? (cash / sum) * 100 : 0;
  const onlinePct = sum ? 100 - cashPct : 0;
  return (
    <div className="db-split">
      <div className="db-split-head">
        <span className="db-split-title">{title}</span>
        <span className="db-split-total">
          {loading ? "—" : formatAmount(total)}
        </span>
      </div>
      <div
        className="db-split-bar"
        role="img"
        aria-label={`${title}: cash ${cashPct.toFixed(0)}%, online ${onlinePct.toFixed(0)}%`}>
        {sum > 0 ? (
          <>
            <div
              className="db-split-seg db-seg-cash"
              style={{ width: `${cashPct}%` }}
              title={`Cash: ${formatAmount(cash)} (${cashPct.toFixed(1)}%)`}
            />
            <div
              className="db-split-seg db-seg-online"
              style={{ width: `${onlinePct}%` }}
              title={`Online: ${formatAmount(online)} (${onlinePct.toFixed(1)}%)`}
            />
          </>
        ) : (
          !loading && <div className="db-split-empty">No transactions yet</div>
        )}
      </div>
      <div className="db-split-legend">
        <span>
          <i className="db-dot db-seg-cash" /> Cash <b>{formatAmount(cash)}</b>{" "}
          <em>{cashPct.toFixed(0)}%</em>
        </span>
        <span>
          <i className="db-dot db-seg-online" /> Online{" "}
          <b>{formatAmount(online)}</b> <em>{onlinePct.toFixed(0)}%</em>
        </span>
      </div>
    </div>
  );
}

function GameCard({ g, index }) {
  const st =
    GAME_STYLE[g.game_id] || GAME_FALLBACK[index % GAME_FALLBACK.length];
  const up = g.profit >= 0;
  return (
    <div className="db-game" style={{ "--g": st.color }}>
      <div className="db-game-top">
        <div className="db-game-icon">{st.icon}</div>
        <div>
          <div className="db-game-name">Win 3X</div>
          <div className="db-game-sub">
            {formatCount(g.bet_count)} bets · {formatCount(g.max_daily_players)}{" "}
            players/day
          </div>
        </div>
        <div className="db-game-profit">
          <b className={up ? "is-up" : "is-down"}>{formatAmount(g.profit)}</b>
          <span>Profit</span>
        </div>
      </div>
      <div className="db-game-stats">
        <div>
          <span>Total Bet</span>
          <b>{formatAmount(g.total_bet)}</b>
        </div>
        <div>
          <span>Total Win</span>
          <b>{formatAmount(g.total_win)}</b>
        </div>
        <div>
          <span>Today's Bet</span>
          <b>{formatAmount(g.todays_bet)}</b>
        </div>
        <div>
          <span>Today's Profit</span>
          <b className={g.todays_profit >= 0 ? "is-up" : "is-down"}>
            {formatAmount(g.todays_profit)}
          </b>
        </div>
      </div>
      <div className="db-game-bar">
        <i style={{ width: `${Math.min(g.payout_ratio, 100)}%` }} />
      </div>
      <div className="db-game-barlabel">
        <span>Payout ratio</span>
        <span>{g.payout_ratio}%</span>
      </div>
    </div>
  );
}

function GamesSection({ games, loading, days, setDays }) {
  const list = games?.games || [];
  const trend = games?.trend || [];
  const chartData = {
    labels: trend.map((t) => t.date.slice(5)),
    datasets: [
      {
        label: "Bets",
        data: trend.map((t) => t.bet),
        borderColor: "#4cc9ff",
        backgroundColor: "#4cc9ff",
        tension: 0.3,
      },
      {
        label: "Wins",
        data: trend.map((t) => t.win),
        borderColor: "#2ee6a6",
        backgroundColor: "#2ee6a6",
        tension: 0.3,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: { legend: { labels: { color: "#ffffffaa" } } },
    scales: {
      x: { ticks: { color: "#ffffff99" }, grid: { color: "#ffffff10" } },
      y: { ticks: { color: "#ffffff99" }, grid: { color: "#ffffff10" } },
    },
  };
  return (
    <section className="db-section">
      <div className="db-section-head">
        <h4>Games</h4>
        <span>Bets, wins and profit per game</span>
        <div className="db-games-tools">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>
      </div>
      {loading ? (
        <div className="db-games-grid">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="db-game">
              <span className="db-skeleton db-skeleton-lg" />
            </div>
          ))}
        </div>
      ) : list.length ? (
        <div className="db-games-grid">
          {list.map((g, i) => (
            <GameCard key={g.game_id} g={g} index={i} />
          ))}
        </div>
      ) : (
        <div className="db-games-empty">No game activity in this period.</div>
      )}
      {trend.length > 0 && (
        <div className="db-trend">
          <Line data={chartData} options={chartOptions} />
        </div>
      )}
    </section>
  );
}

// Ticks every second in its own component so the rest of the dashboard doesn't re-render.
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="db-clock">
      <FaClock />
      {now.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })}
    </span>
  );
}

function Dashboard() {
  const { UnauthorizedLogOut, userdata } = useContext(Context);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState(null);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [days, setDays] = useState(7);
  const getGames = async () => {
    try {
      setGamesLoading(true);
      const response = await findGames(days);
      setGameData(response?.data?.data);
    } catch (error) {
      console.error(error.message);
    } finally {
      setGamesLoading(false);
    }
  };
  useEffect(() => {
    getGames();
  }, [days]);
  const getDataAll = async () => {
    try {
      const response = await findAll();
      setData(response?.data);
    } catch (error) {
      console.error(error.message);
      await UnauthorizedLogOut(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getDataAll();
  }, []);

  const d = data?.data || {};
  const roleId = Number(userdata?.role_id);
  const isRole = (...ids) => ids.includes(roleId);
  const only = (cond, item) => (cond ? [item] : []);

  const today = new Date().toISOString().split("T")[0];
  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const network = [
    {
      label: isRole(1) ? "All Users (Platform)" : "All Users (Downline)",
      value: formatCount(d.total_all_users),
      icon: FaUsers,
      to: "/agent-management/customer",
    },
    {
      label: "Active Users (All Roles)",
      value: formatCount(d.total_all_active_users),
      icon: FaUsers,
      to: "/agent-management/customer?status=1",
      accent: "in",
    },
    {
      label: "Total Customers",
      value: formatCount(d.total_users),
      icon: FaUsers,
      to: "/agent-management/customer",
    },
    {
      label: "Active Customers",
      value: formatCount(d.total_active_users),
      icon: FaUsers,
      to: "/agent-management/customer?status=1",
      accent: "in",
    },
    {
      label: "In-Active Customers",
      value: formatCount(d.total_inactive_users),
      icon: FaUserSlash,
      to: "/agent-management/customer?status=0",
      accent: "muted",
    },
    ...only(isRole(1), {
      label: "Super Super Masters",
      value: formatCount(d.total_super_super_master),
      icon: FaUserShield,
      to: "/agent-management/super-super-master",
    }),
    ...only(isRole(1, 2), {
      label: "Super Masters",
      value: formatCount(d.total_super_master),
      icon: FaSitemap,
      to: "/agent-management/super-master",
    }),
    ...only(isRole(1, 2, 3), {
      label: "Masters",
      value: formatCount(d.total_master),
      icon: FaUserTie,
      to: "/agent-management/master",
    }),

    {
      label: "Change Password Request",
      value: formatCount(d.change_password_request),
      icon: FaUsers,
      to: "/agent-management/password-change-request",
    },
  ];

  const balances = [
    ...only(!!d.total_agency_balance, {
      label: "Justbooking Balance",
      value: formatAmount(d.total_agency_balance),
      icon: FaBuilding,
      to: "/dashboard",
    }),
    ...only(!!d.agents_wallet, {
      label: "Agents Wallet Balance",
      value: formatAmount(d.agents_wallet),
      icon: FaWallet,
      to: "/dashboard",
    }),
    ...only(!!d.customers_wallet, {
      label: "Customers Wallet Balance",
      value: formatAmount(d.customers_wallet),
      icon: FaWallet,
      to: "/dashboard",
    }),
    ...only(isRole(1), {
      label: "Total Wallet Points",
      value: formatAmount(d.total_wallet_amount),
      icon: FaCoins,
      to: "/wallet/history",
    }),
    ...only(isRole(1), {
      label: "Super Super Master Points",
      value: formatAmount(d.total_super_super_master_amount),
      icon: FaUserShield,
      to: "/wallet/history?role_type=2",
    }),
    ...only(isRole(1), {
      label: "Super Master Points",
      value: formatAmount(d.total_super_master_amount),
      icon: FaSitemap,
      to: "/wallet/history?role_type=3",
    }),
    ...only(isRole(1), {
      label: "Master Points",
      value: formatAmount(d.total_master_amount),
      icon: FaUserTie,
      to: "/wallet/history?role_type=4",
    }),
    ...only(isRole(1), {
      label: "Customer Points",
      value: formatAmount(d.total_customer_amount),
      icon: FaUsers,
      to: "/wallet/history?role_type=5",
    }),
  ];

  const games = [
    ...only(isRole(1), {
      label: "Total Matka Bids",
      value: formatAmount(d.total_matka_bit_amount),
      icon: FaDice,
      to: "/matka-bid-history",
    }),
  ];

  const deposits = [
    {
      label: "Today's Deposits",
      value: formatAmount(d.todays_deposite_money),
      icon: FaClock,
      to: `/wallet/history?wallet_type=3,9&date=${today}`,
      accent: "in",
    },
    {
      label: "Cash Deposits",
      value: formatAmount(d.total_cash_deposite_money),
      icon: FaMoneyBillWave,
      to: "/wallet/history?wallet_type=9",
      accent: "in",
    },
    {
      label: "Online Deposits",
      value: formatAmount(d.total_online_deposite_money),
      icon: FaUniversity,
      to: "/wallet/history?wallet_type=3",
      accent: "in",
    },
    {
      label: "Total Deposits",
      value: formatAmount(d.total_deposite_money),
      icon: FaArrowDown,
      to: "/wallet/history?wallet_type=3",
      accent: "in",
    },
  ];

  const withdrawals = [
    {
      label: "Today's Withdrawals",
      value: formatAmount(d.todays_withdraw_money),
      icon: FaClock,
      to: `/wallet/accept?date=${today}`,
      accent: "out",
    },
    {
      label: "Cash Withdrawals",
      value: formatAmount(d.total_cash_withdraw_money),
      icon: FaMoneyBillWave,
      to: "/wallet/history?wallet_type=10",
      accent: "out",
    },
    {
      label: "Online Withdrawals",
      value: formatAmount(d.total_online_withdraw_money),
      icon: FaUniversity,
      to: "/wallet/history?wallet_type=12",
      accent: "out",
    },
    {
      label: "Total Withdrawals",
      value: formatAmount(d.total_withdraw_money),
      icon: FaArrowUp,
      to: "/wallet/history?wallet_type=1,10,12",
      accent: "out",
    },
  ];

  const totalDeposits = Number(d.total_deposite_money) || 0;
  const totalWithdrawals = Number(d.total_withdraw_money) || 0;
  const netFlow = totalDeposits - totalWithdrawals;

  return (
    <section className="Dashboard db">
      {/* ---------- Header ---------- */}
      <div className="db-header">
        <div>
          <span className="db-eyebrow">{todayLabel}</span>
          <h2>Welcome Back{userdata?.name ? `, ${userdata.name}` : ""}</h2>
          <p>Here's what's happening across your tables today.</p>
        </div>
        <div className="db-header-side">
          <LiveClock />
          {ROLE_NAMES[roleId] && (
            <span className="db-role">{ROLE_NAMES[roleId]}</span>
          )}
        </div>
      </div>

      {/* ---------- Hero KPIs ---------- */}
      <div className="db-hero">
        <Link
          to="/wallet/history"
          state={{ name: userdata?.name }}
          className="db-hero-main">
          <span className="db-hero-label">
            <FaWallet /> My Wallet
          </span>
          {loading ? (
            <span className="db-skeleton db-skeleton-lg" />
          ) : (
            <span className="db-hero-value">
              {formatAmount(d.my_wallet_amount)}
            </span>
          )}
          <span className="db-hero-link">
            View history <FaChevronRight />
          </span>
        </Link>

        <Link to="/wallet/request" className="db-hero-tile db-hero-alert">
          <span className="db-hero-label">
            <FaHandHoldingUsd /> Pending Withdraw Requests
          </span>
          {loading ? (
            <span className="db-skeleton" />
          ) : (
            <span className="db-hero-tile-value">
              {formatAmount(d.total_requested_withdraw)}
            </span>
          )}
          <span className="db-hero-link">
            Review requests <FaChevronRight />
          </span>
        </Link>
      </div>

      {/* ---------- Money flow ---------- */}
      <section className="db-section db-flow">
        <div className="db-section-head">
          <h4>Points Flow</h4>
          <span>All-time, cash vs online</span>
        </div>
        <div className="db-flow-body">
          <div className="db-net">
            <span className="db-hero-label">
              Net Flow (Deposits − Withdrawals)
            </span>
            <span
              className={`db-net-value ${netFlow < 0 ? "is-negative" : ""}`}>
              {loading
                ? "—"
                : `${netFlow < 0 ? "− " : ""}${formatAmount(Math.abs(netFlow))}`}
            </span>
          </div>
          <SplitBar
            title="Deposits"
            cash={Number(d.total_cash_deposite_money) || 0}
            online={Number(d.total_online_deposite_money) || 0}
            total={totalDeposits}
            loading={loading}
          />
          <SplitBar
            title="Withdrawals"
            cash={Number(d.total_cash_withdraw_money) || 0}
            online={Number(d.total_online_withdraw_money) || 0}
            total={totalWithdrawals}
            loading={loading}
          />
        </div>
      </section>

      <GamesSection
        games={gameData}
        loading={gamesLoading}
        days={days}
        setDays={setDays}
      />

      <Section
        title="Network"
        subtitle="Players and agents under you"
        items={network}
        loading={loading}
      />
      <Section
        title="Balances"
        subtitle="Wallet holdings"
        items={balances}
        loading={loading}
      />
      {/* <Section title="Games" items={games} loading={loading} /> */}
      <div className="row g-4">
        <div className="col-xl-6">
          <Section title="Deposits" items={deposits} loading={loading} />
        </div>
        <div className="col-xl-6">
          <Section title="Withdrawals" items={withdrawals} loading={loading} />
        </div>
      </div>
    </section>
  );
}

export default Dashboard;
